import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import ConsoleDrawer from '../components/ConsoleDrawer';
import ModuleCard from '../components/ModuleCard';
import { getModules } from '../lib/apiClient';
import { getStudyHub } from '../lib/studyApi';

// Legacy ActionPill retained only for previewMode fallback / exit link.
const ActionPill = ({ to, href, external, testid, title, children }) => {
  const cls = 'btn-outline text-sm';
  if (external && href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
         className={cls} data-testid={testid} title={title}>
        {children}
      </a>
    );
  }
  return (
    <a href={to} className={cls} data-testid={testid} title={title}>
      {children}
    </a>
  );
};

/**
 * Faculty Console.
 *   - Native: rendered at /teacher for users with role=teacher.
 *   - Preview: rendered at /preview-as-faculty for developers so they can
 *     verify what faculty see. Pass `previewMode` + `exitPath` props.
 */
const TeacherDashboard = ({ previewMode = false, exitPath = '/teacher' }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decksByModuleId, setDecksByModuleId] = useState({});

  useEffect(() => {
    getModules().then((m) => setModules(m)).finally(() => setLoading(false));
    getStudyHub()
      .then((hub) => {
        const byId = {};
        for (const d of (hub.decks || [])) {
          if (d.module_id) byId[d.module_id] = d;
        }
        setDecksByModuleId(byId);
      })
      .catch(() => setDecksByModuleId({}));
  }, []);

  return (
    <div className="min-h-screen" data-testid={previewMode ? 'faculty-preview' : 'teacher-dashboard'}>
      <Navbar subtitle="Professor / TA" />

      {previewMode && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              <span className="font-semibold">Preview as Faculty</span>
              <span className="ml-2 opacity-80">— showing the Faculty Console without developer-only tools.</span>
            </div>
            <ActionPill to={exitPath} testid="exit-faculty-preview-link">
              Exit preview ↩
            </ActionPill>
          </div>
        </div>
      )}

      {previewMode ? (
        <FacultyWorkspace previewMode modules={modules} loading={loading} decksByModuleId={decksByModuleId} />
      ) : (
        <ConsoleDrawer role="faculty">
          <FacultyWorkspace modules={modules} loading={loading} decksByModuleId={decksByModuleId} />
        </ConsoleDrawer>
      )}
      {/* AI Tutor now lives inside ConsoleDrawer's tutor pill (voice + text).
          Legacy floating ChatPanel bubble removed to eliminate the double UI. */}
    </div>
  );
};

function FacultyWorkspace({ previewMode = false, modules, loading, decksByModuleId }) {
  return (
    <main className={`max-w-7xl mx-auto px-6 py-10 ${previewMode ? '' : 'pl-16'}`}>
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {previewMode ? 'Faculty Console (Preview)' : 'Faculty Console'}
          </h1>
          <p className="text-gray-500 mt-1">
            Class metrics, Socratic Pathways, authenticity roll-ups, and your course modules.
          </p>
        </div>
      </div>

      {/* Nav has moved into the ConsoleDrawer. The 3 action-group pill rows previously
          rendered here (Class & Grading, Faculty Telescopes, Views & Resources) are now
          delivered via the drawer. Retained: course modules grid below. */}

      {/* Modules */}
      <h2 className="text-lg font-bold text-gray-800 mb-3">Course Modules</h2>
      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="teacher-modules-grid">
          {modules.map((m) => (
            <ModuleCard key={m.id} module={m} viewerRolePath="/teacher" canEdit={false}
                        studyDeck={decksByModuleId[m.id] || null} />
          ))}
        </div>
      )}
    </main>
  );
}

export default TeacherDashboard;
